/**
 * FIELD FORMATTING — pure helpers for per-field visual emphasis, editable from the
 * inline layout editor and Admin → Field Builder.
 *
 * A field may carry a `format` object (stored in settings/fieldLayout — an override for
 * base fields, the field's own record for custom fields):
 *
 *   format: {
 *     bold:       true | false          — label weight (default font-medium)
 *     labelSize:  'default' | 'large'   — label text size
 *     background: 'none' | 'gray' | 'blue' | 'amber'
 *     border:     'none' | 'box'
 *   }
 *
 * Class strings are deliberately STATIC literals (looked up from maps) so Tailwind's
 * scanner sees every class at build time — never assemble class names dynamically.
 *
 * Also home to the "which fields have admin-editable choices?" rule shared by both
 * editors: only option-backed types whose options are a static array. Fields whose
 * choices come from live sources (roster, practice groups, areas of law, file subtypes
 * via `optionsFrom`) are managed on their own admin pages, not here.
 */

/** Field types whose choices are an admin-defined `options` array. */
export const OPTION_FIELD_TYPES = ['select', 'radio', 'multicheck', 'optionsMultiSelect'];

export const FORMAT_BACKGROUNDS = [
  ['none', 'No background'],
  ['gray', 'Gray background'],
  ['blue', 'Blue background'],
  ['amber', 'Amber highlight'],
];

export const FORMAT_BORDERS = [
  ['none', 'No border'],
  ['box', 'Boxed'],
];

export const FORMAT_LABEL_SIZES = [
  ['small', 'Small text'],
  ['default', 'Normal text'],
  ['large', 'Large text'],
  ['xlarge', 'Extra large text'],
];

/** Notice banner styles. 'plain' renders bare text — no border, background, or box. */
export const NOTICE_VARIANTS = [
  ['warning', 'Warning (yellow)'],
  ['neutral', 'Neutral (gray)'],
  ['plain', 'Plain (no border or background)'],
];

const BACKGROUND_CLASS = {
  gray: 'bg-slate-100 dark:bg-slate-800',
  blue: 'bg-firm-50 dark:bg-firm-950',
  amber: 'bg-amber-50 dark:bg-amber-950',
};

const BORDER_CLASS = {
  box: 'border border-slate-300 dark:border-slate-600',
};

/** Text sizes, as static Tailwind literals (see the note above about the scanner). */
const TEXT_SIZE_CLASS = {
  small: 'text-xs',
  default: 'text-sm',
  large: 'text-base',
  xlarge: 'text-lg',
};

/** Tolerates a missing/partial/malformed format object. */
export function normalizeFormat(format) {
  const source = format && typeof format === 'object' ? format : {};
  return {
    bold: source.bold === true,
    italic: source.italic === true,
    underline: source.underline === true,
    labelSize: TEXT_SIZE_CLASS[source.labelSize] ? source.labelSize : 'default',
    background: BACKGROUND_CLASS[source.background] ? source.background : 'none',
    border: BORDER_CLASS[source.border] ? source.border : 'none',
  };
}

/**
 * The text classes for a NOTICE banner's body (size / weight / italic / underline).
 * A notice has no label — its text IS the content — so its formatting applies here
 * rather than through formatLabelClasses. Colour comes from the notice's variant.
 */
export function formatNoticeTextClasses(field) {
  const f = normalizeFormat(field?.format);
  return [
    TEXT_SIZE_CLASS[f.labelSize],
    f.bold ? 'font-bold' : 'font-medium',
    f.italic ? 'italic' : '',
    f.underline ? 'underline' : '',
  ]
    .filter(Boolean)
    .join(' ');
}

/** True when the field carries any non-default formatting. */
export function hasFormat(field) {
  const f = normalizeFormat(field?.format);
  return f.bold || f.labelSize !== 'default' || f.background !== 'none' || f.border !== 'none';
}

/**
 * Extra classes for the field's wrapper. Padding + rounding apply only when a visible
 * treatment (background or border) is on, so unformatted fields render byte-for-byte
 * as before.
 */
export function formatWrapperClasses(field) {
  const f = normalizeFormat(field?.format);
  const parts = [];
  if (f.background !== 'none') parts.push(BACKGROUND_CLASS[f.background]);
  if (f.border !== 'none') parts.push(BORDER_CLASS[f.border]);
  if (parts.length) parts.push('rounded', 'p-3');
  return parts.join(' ');
}

/** The complete label class string (size + weight + emphasis are format-aware). */
export function formatLabelClasses(field) {
  const f = normalizeFormat(field?.format);
  return [
    'block',
    TEXT_SIZE_CLASS[f.labelSize],
    f.bold ? 'font-bold' : 'font-medium',
    f.italic ? 'italic' : '',
    f.underline ? 'underline' : '',
    'text-slate-700 dark:text-slate-200',
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * Whether an admin may edit this field's choices from the layout editors.
 * Custom option fields always own their options. Base fields qualify only when the code
 * schema ships a static options array (no `optionsFrom` live source) — those lists are
 * data the firm may retune without a deploy, exactly like a custom field's.
 */
export function hasEditableOptions(field) {
  if (!field || !OPTION_FIELD_TYPES.includes(field.type)) return false;
  if (field.optionsFrom) return false;
  return Array.isArray(field.options);
}
